import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { AuthContext } from './context/AuthContext';
import { initiateSocket, getSocket } from './socket';
import axiosInstance from './axiosInstance';
import ProtectedComponent from './ProtectedComponent';
import './styles/WebhookCallButton.css';

// Audio unlock state - persists across re-renders (exact same pattern as WebhookMonitor)
let audioUnlocked = false;
let audioInstance = null;

function WebhookCallButton() {
  const { user } = useContext(AuthContext);
  const [alerts, setAlerts] = useState([]);
  const [activeAlert, setActiveAlert] = useState(null);
  const [connected, setConnected] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [filter, setFilter] = useState('');
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [responding, setResponding] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [resetMinutes, setResetMinutes] = useState(1); // Default 1 minute
  const terminalRef = useRef(null);

  // Unlock audio on first user interaction - exact same pattern as WebhookMonitor
  useEffect(() => {
    if (audioUnlocked) return;

    const unlockAudio = () => {
      if (audioUnlocked) return;

      // Create alert sound
      audioInstance = new Audio('data:audio/wav;base64,UklGRl9vT19teleVpZAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2+telZVpZmsmujX1UH0CAlsK8y7eNYB88YJPYzFtKnb6BXCA3m53e2rix');
      audioInstance.volume = 0;
      audioInstance.play().then(() => {
        audioInstance.pause();
        audioInstance.currentTime = 0;
        audioInstance.volume = 0.7;
        audioUnlocked = true;
        console.log('[WebhookCallButton] Audio unlocked via user interaction');
      }).catch(() => {});

      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('keydown', unlockAudio);
    };

    document.addEventListener('click', unlockAudio);
    document.addEventListener('keydown', unlockAudio);

    return () => {
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('keydown', unlockAudio);
    };
  }, []);

  // Play alert sound
  const playAlertSound = useCallback(() => {
    if (!soundEnabled) return;

    if (audioUnlocked && audioInstance) {
      audioInstance.currentTime = 0;
      audioInstance.play().catch(e => console.log('Audio play blocked:', e));
    }
  }, [soundEnabled]);

  // Get the webhook URL to display to user
  const webhookUrl = `${window.location.origin}/api/webhook-callbutton/receive`;

  // WebSocket setup - EXACT same pattern as WebhookMonitor (no rooms)
  useEffect(() => {
    loadHistory();
    initiateSocket();
    const socket = getSocket();

    if (!socket) {
      console.warn('[WebhookCallButton] Socket not available');
      return;
    }

    setConnected(socket.connected);

    // Handler for incoming call button alerts
    const handleCallButtonReceived = (data) => {
      console.log('[WebhookCallButton] callbutton:received', data);
      setAlerts(prev => [data, ...prev].slice(0, 100));

      // Show modal popup for new pending alerts
      if (data.status === 'pending') {
        setActiveAlert(data);
        playAlertSound();
      }
    };

    // Handler for responded alerts
    const handleCallButtonResponded = (data) => {
      console.log('[WebhookCallButton] callbutton:responded', data);
      setAlerts(prev =>
        prev.map(a => a.id === data.id ? { ...a, ...data } : a)
      );

      // Close modal if this was the active alert
      setActiveAlert(prev => prev?.id === data.id ? null : prev);
    };

    const handleCleared = () => {
      setAlerts([]);
      setActiveAlert(null);
    };

    socket.on('connect', () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));
    socket.on('callbutton:received', handleCallButtonReceived);
    socket.on('callbutton:responded', handleCallButtonResponded);
    socket.on('callbutton:cleared', handleCleared);

    return () => {
      socket.off('connect');
      socket.off('disconnect');
      socket.off('callbutton:received', handleCallButtonReceived);
      socket.off('callbutton:responded', handleCallButtonResponded);
      socket.off('callbutton:cleared', handleCleared);
    };
  }, [playAlertSound]);

  // Auto-scroll to top when new alerts arrive
  useEffect(() => {
    if (autoScroll && terminalRef.current) {
      terminalRef.current.scrollTop = 0;
    }
  }, [alerts, autoScroll]);

  const loadHistory = async () => {
    try {
      const { data } = await axiosInstance.get('/webhook-callbutton/history?limit=50');
      if (data.success) {
        setAlerts(data.alerts);
      }
    } catch (err) {
      console.error('Failed to load call button history:', err);
    }
  };

  const clearHistory = async () => {
    try {
      await axiosInstance.delete('/webhook-callbutton/history');
      setAlerts([]);
      setActiveAlert(null);
    } catch (err) {
      console.error('Failed to clear history:', err);
    }
  };

  const handleRespond = async (response) => {
    if (!activeAlert || responding) return;

    setResponding(true);
    try {
      const { data } = await axiosInstance.post('/webhook-callbutton/respond', {
        alertId: activeAlert.id,
        response, // 'coming' or 'not_coming'
        resetMinutes, // How long before resetting to WHTEST
        // Pass label info so server can update the label
        companyCode: activeAlert.companyCode,
        storeCode: activeAlert.storeCode,
        articleId: activeAlert.articleId,
        labelCode: activeAlert.labelCode,
        area: activeAlert.area
      });

      if (data.success) {
        console.log('[WebhookCallButton] Response sent:', data);
        setActiveAlert(null);
      } else {
        console.error('[WebhookCallButton] Response failed:', data.message);
        alert('Response failed: ' + data.message);
      }
    } catch (err) {
      console.error('Failed to respond:', err);
      alert('Failed to send response');
    } finally {
      setResponding(false);
    }
  };

  const handleDismiss = () => {
    setActiveAlert(null);
  };

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const formatTimestamp = (iso) => {
    const date = new Date(iso);
    return date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#ffaa00';
      case 'acknowledged': return '#00ff88';
      case 'declined': return '#ff6b6b';
      default: return '#888888';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'PENDING';
      case 'acknowledged': return 'ON THE WAY';
      case 'declined': return 'DECLINED';
      default: return status?.toUpperCase() || 'UNKNOWN';
    }
  };

  const filteredAlerts = alerts.filter(alert => {
    if (!filter) return true;
    const searchStr = JSON.stringify(alert).toLowerCase();
    return searchStr.includes(filter.toLowerCase());
  });

  return (
    <ProtectedComponent functionCode="SA11" functionName="Webhook Call Button">
      <div className="call-button-monitor">
        {/* Active Alert Modal Popup */}
        {activeAlert && (
          <div className="call-button-modal-overlay">
            <div className="call-button-modal">
              <div className="modal-header">
                <div className="alert-icon pulse">!</div>
                <h2>Help Needed!</h2>
              </div>

              <div className="modal-body">
                <div className="area-display">
                  <span className="area-label">Area</span>
                  <span className="area-value">{activeAlert.area}</span>
                </div>

                <div className="alert-details">
                  {activeAlert.storeCode && (
                    <div className="detail-row">
                      <span className="detail-label">Store:</span>
                      <span className="detail-value">{activeAlert.storeCode}</span>
                    </div>
                  )}
                  {activeAlert.labelCode && (
                    <div className="detail-row">
                      <span className="detail-label">Label:</span>
                      <span className="detail-value">{activeAlert.labelCode}</span>
                    </div>
                  )}
                  <div className="detail-row">
                    <span className="detail-label">Time:</span>
                    <span className="detail-value">{formatTimestamp(activeAlert.timestamp)}</span>
                  </div>
                </div>

                <div className="reset-timer-select">
                  <label>Reset after:</label>
                  <select
                    value={resetMinutes}
                    onChange={(e) => setResetMinutes(Number(e.target.value))}
                  >
                    <option value={0.5}>30 seconds</option>
                    <option value={1}>1 minute</option>
                    <option value={5}>5 minutes</option>
                  </select>
                </div>
              </div>

              <div className="modal-actions">
                <button
                  className="response-btn coming"
                  onClick={() => handleRespond('coming')}
                  disabled={responding}
                >
                  {responding ? 'Sending...' : 'Coming'}
                </button>
                <button
                  className="response-btn not-coming"
                  onClick={() => handleRespond('not_coming')}
                  disabled={responding}
                >
                  Not Coming
                </button>
              </div>

              <button className="dismiss-btn" onClick={handleDismiss}>
                Dismiss
              </button>
            </div>
          </div>
        )}

        <div className="call-button-header">
          <h2>Webhook Call Button</h2>
          <div className="header-controls">
            <label className="sound-toggle">
              <input
                type="checkbox"
                checked={soundEnabled}
                onChange={(e) => setSoundEnabled(e.target.checked)}
              />
              Sound
            </label>
            <div className="call-button-status">
              <span className={`status-dot ${connected ? 'connected' : 'disconnected'}`}></span>
              {connected ? 'Live' : 'Disconnected'}
            </div>
          </div>
        </div>

        <div className="call-button-config">
          <div className="config-row">
            <label>Webhook URL:</label>
            <div className="url-display">
              <code>{webhookUrl}</code>
              <button
                className="copy-btn"
                onClick={() => copyToClipboard(webhookUrl)}
                title="Copy URL"
              >
                Copy
              </button>
            </div>
          </div>
          <p className="config-hint">
            Configure this URL in your SOLUM SaaS Webhook Subscription settings for button press events
          </p>
        </div>

        <div className="call-button-controls">
          <input
            type="text"
            placeholder="Filter alerts..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="filter-input"
          />
          <label className="auto-scroll-toggle">
            <input
              type="checkbox"
              checked={autoScroll}
              onChange={(e) => setAutoScroll(e.target.checked)}
            />
            Auto-scroll
          </label>
          <button onClick={loadHistory} className="refresh-btn">
            Refresh
          </button>
          <button onClick={clearHistory} className="clear-btn">
            Clear
          </button>
        </div>

        <div className="call-button-terminal" ref={terminalRef}>
          {filteredAlerts.length === 0 ? (
            <div className="terminal-empty">
              <span className="prompt">&gt;</span> Waiting for call button events...
              <span className="cursor">_</span>
            </div>
          ) : (
            filteredAlerts.map((alert) => {
              const isExpanded = expandedIds.has(alert.id);

              return (
                <div
                  key={alert.id}
                  className={`alert-entry ${isExpanded ? 'expanded' : ''} ${alert.status}`}
                  onClick={() => toggleExpand(alert.id)}
                >
                  <div className="alert-summary">
                    <span className="timestamp">[{formatTimestamp(alert.timestamp)}]</span>
                    <span
                      className="status-badge"
                      style={{ color: getStatusColor(alert.status) }}
                    >
                      {getStatusLabel(alert.status)}
                    </span>
                    <span className="area-preview">
                      {alert.area}
                    </span>
                    {alert.respondedBy && (
                      <span className="responder">
                        by {alert.respondedBy}
                      </span>
                    )}
                    <span className="expand-hint">{isExpanded ? '▼' : '▶'}</span>
                  </div>

                  {isExpanded && (
                    <div className="alert-details-expanded">
                      <div className="detail-section">
                        <div className="detail-header">Alert Info</div>
                        <pre>
{JSON.stringify({
  area: alert.area,
  labelCode: alert.labelCode,
  storeCode: alert.storeCode,
  companyCode: alert.companyCode,
  articleId: alert.articleId,
  status: alert.status,
  respondedBy: alert.respondedBy,
  respondedAt: alert.respondedAt
}, null, 2)}
                        </pre>
                      </div>
                      <div className="detail-section">
                        <div className="detail-header">
                          Raw Body
                          <button
                            className="copy-small"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(JSON.stringify(alert.body, null, 2));
                            }}
                          >
                            Copy
                          </button>
                        </div>
                        <pre>{JSON.stringify(alert.body, null, 2)}</pre>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="call-button-footer">
          <span>{filteredAlerts.length} alert(s)</span>
          {filter && <span> (filtered from {alerts.length})</span>}
        </div>
      </div>
    </ProtectedComponent>
  );
}

export default WebhookCallButton;
